import { useMemo, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  F0Box,
  F0Dialog,
  F0Form,
  F0Text,
  f0FormField,
  useF0Form,
  F0Select,
  F0SelectItemObject,
} from '@factorialco/f0-react'
import type { CustomFieldRenderProps } from '@factorialco/f0-react'
import { Input, NumberInput, Switch } from '@factorialco/f0-react/dist/experimental'
import {
  EditExpenseBudgetFragment,
  GetEmployeesForBudgetsDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useArchiveBudget,
  useBudgetsPage,
  useUnarchiveBudget,
  useUpdateBudget,
} from 'modules/finance/hooks/useBudgets'
import { periodicityOptions } from 'modules/finance/lib/budgets'

interface Props {
  budget: EditExpenseBudgetFragment
  onClose: () => void
  onSuccess: () => void
}

export const EditBudgetSidepanel = ({ budget, onClose, onSuccess }: Props) => {
  const [archived, setArchived] = useState(budget.archived)
  const archivedRef = useRef(archived)
  archivedRef.current = archived

  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { legalEntities } = useBudgetsPage(companyId)

  const { updateBudget } = useUpdateBudget()
  const { archiveBudget } = useArchiveBudget()
  const { unarchiveBudget } = useUnarchiveBudget()

  const { data: employeesData, loading: loadingEmployees } = useQuery(
    GetEmployeesForBudgetsDocument,
    {}
  )

  const legalEntity = legalEntities?.find((node) => node.id === budget.legalEntityId)

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const canEdit = budget._policies?.update.can
  const disableFields = budget.archived || !canEdit

  const employeeOptions: F0SelectItemObject<string>[] = useMemo(() => {
    const employees = employeesData?.employees.employees || []
    return employees.map((employee) => ({
      value: employee.id,
      label: employee.fullName ?? '',
    }))
  }, [employeesData])

  const periodicitySelectOptions: F0SelectItemObject<string>[] = useMemo(
    () =>
      periodicityOptions().map((option) => ({
        value: option.value,
        label: option.label,
      })),
    []
  )

  const schema = useMemo(
    () =>
      z
        .object({
          name: f0FormField(z.string().min(1).max(280), {
            label: i18n.t('budgets.forms.new_budget.basic_information.budget_title'),
            placeholder: i18n.t('budgets.forms.new_budget.basic_information.budget_title'),
            disabled: !canEdit,
          }),
          description: f0FormField(z.string().max(1000).optional(), {
            label: i18n.t('budgets.forms.new_budget.basic_information.budget_description'),
            placeholder: i18n.t('budgets.forms.new_budget.basic_information.budget_description'),
            fieldType: 'textarea',
            disabled: disableFields,
          }),
          ownerEmployeeIds: f0FormField(z.array(z.string()).optional(), {
            label: i18n.t('budgets.forms.new_budget.budget_access.budget_owners'),
            fieldType: 'custom',
            render: ({
              value,
              onChange,
              disabled,
            }: CustomFieldRenderProps<string[] | undefined>) => (
              <F0Box display="flex" flexDirection="column" gap="xs">
                <F0Text
                  variant="label"
                  content={i18n.t('budgets.forms.new_budget.budget_access.budget_owners')}
                />
                <F0Text
                  variant="body"
                  color="secondary"
                  content={i18n.t(
                    'budgets.forms.new_budget.budget_access.budget_owners_description'
                  )}
                />
                <F0Select
                  multiple
                  placeholder={i18n.t('budgets.forms.new_budget.budget_access.select_employees')}
                  items={employeeOptions}
                  value={value ?? []}
                  onChange={onChange}
                  disabled={disabled || disableFields}
                />
              </F0Box>
            ),
          }),
          viewerEmployeeIds: f0FormField(z.array(z.string()).optional(), {
            label: i18n.t('budgets.forms.new_budget.budget_access.budget_viewers'),
            fieldType: 'custom',
            render: ({
              value,
              onChange,
              disabled,
            }: CustomFieldRenderProps<string[] | undefined>) => (
              <F0Box display="flex" flexDirection="column" gap="xs">
                <F0Text
                  variant="label"
                  content={i18n.t('budgets.forms.new_budget.budget_access.budget_viewers')}
                />
                <F0Text
                  variant="body"
                  color="secondary"
                  content={i18n.t(
                    'budgets.forms.new_budget.budget_access.budget_viewers_description'
                  )}
                />
                <F0Select
                  multiple
                  placeholder={i18n.t('budgets.forms.new_budget.budget_access.select_employees')}
                  items={employeeOptions}
                  value={value ?? []}
                  onChange={onChange}
                  disabled={disabled || disableFields}
                />
              </F0Box>
            ),
          }),
          effectiveFrom: f0FormField(z.date(), {
            label: i18n.t('budgets.forms.new_budget.budget_limits.start_date'),
            placeholder: i18n.t('budgets.forms.new_budget.budget_limits.start_date'),
            row: 'dates',
            disabled: disableFields,
          }),
          effectiveUntil: f0FormField(z.date().optional(), {
            label: i18n.t('budgets.forms.new_budget.budget_limits.end_date'),
            placeholder: i18n.t('budgets.forms.new_budget.budget_limits.end_date'),
            row: 'dates',
            disabled: disableFields,
            minDate: ({ values }: { values: Record<string, unknown> }) =>
              values.effectiveFrom ? new Date(`${values.effectiveFrom}`) : undefined,
          }),
          periodicity: f0FormField(z.string(), {
            label: i18n.t('budgets.forms.new_budget.budget_limits.time_frame'),
            fieldType: 'custom',
            render: ({ value }: CustomFieldRenderProps<string>) => (
              <F0Select
                disabled
                label={i18n.t('budgets.forms.new_budget.budget_limits.time_frame')}
                items={periodicitySelectOptions}
                value={value}
                onChange={() => undefined}
              />
            ),
          }),
          amount: f0FormField(z.number().positive(), {
            label: i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame'),
            fieldType: 'custom',
            render: ({ value, onChange, disabled }: CustomFieldRenderProps<number>) => (
              <NumberInput
                required
                label={i18n.t('budgets.forms.new_budget.budget_limits.amount_per_time_frame')}
                locale={i18n.locale}
                units={budget.currency}
                value={value ?? null}
                onChange={(v: number | null) => onChange(v ?? 0)}
                disabled={disabled || disableFields}
                maxDecimals={2}
                size="md"
              />
            ),
          }),
        })
        .superRefine((data, ctx) => {
          if (
            data.effectiveFrom &&
            data.effectiveUntil &&
            data.effectiveFrom > data.effectiveUntil
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t('budgets.forms.errors.start_date'),
              path: ['effectiveFrom'],
            })
          }
        }),
    [budget.currency, canEdit, disableFields, employeeOptions, periodicitySelectOptions]
  )

  const defaultValues = useMemo(
    () => ({
      name: budget.name,
      description: budget.description ?? '',
      ownerEmployeeIds: budget.ownerEmployees?.map((e) => e.id) ?? [],
      viewerEmployeeIds: budget.viewerEmployees?.map((e) => e.id) ?? [],
      effectiveFrom: new Date(String(budget.effectiveFrom)),
      effectiveUntil: budget.effectiveUntil ? new Date(String(budget.effectiveUntil)) : undefined,
      periodicity: budget.periodicity ?? 'monthly',
      amount: fromCents(Number(budget.amountCents)),
    }),
    [budget]
  )

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    const result = await updateBudget({
      id: budget.id,
      legalEntityId: budget.legalEntityId,
      name: data.name,
      description: data.description,
      amountCents: toCents(data.amount),
      effectiveFrom: data.effectiveFrom.toISOString(),
      effectiveUntil: data.effectiveUntil?.toISOString() ?? null,
      ownerEmployeeIds: data.ownerEmployeeIds,
      viewerEmployeeIds: data.viewerEmployeeIds,
    })

    let formErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      formErrors = errors
    })

    if (!handled.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.forms.edit_budget.flash.budget_updated_error'),
      })
      return { success: false as const, errors: formErrors }
    }

    const archiveChanged = archivedRef.current !== budget.archived

    if (archiveChanged) {
      if (archivedRef.current) {
        const archiveResult = await archiveBudget({ id: budget.id })
        const archiveHandled = handleResult(archiveResult, () => undefined)

        if (!archiveHandled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('budgets.forms.edit_budget.flash.budget_archive_error'),
          })
          return { success: false as const }
        }
      } else {
        const unarchiveResult = await unarchiveBudget({ id: budget.id })
        const unarchiveHandled = handleResult(unarchiveResult, () => undefined)

        if (!unarchiveHandled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('budgets.forms.edit_budget.flash.budget_unarchive_error'),
          })
          return { success: false as const }
        }
      }
    }

    app.setFlash({
      type: 'correct',
      message: i18n.t('budgets.forms.edit_budget.flash.budget_updated_ok'),
    })
    onSuccess()
    return { success: true as const }
  }

  return (
    <F0Dialog
      isOpen
      position="right"
      title={i18n.t('budgets.forms.edit_budget.title')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('common.save'),
        onClick: submit,
        disabled: isSubmitting || hasErrors || !canEdit || loadingEmployees,
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="lg">
        {/* Basic Information Section */}
        <F0Box display="flex" flexDirection="column" gap="sm">
          <F0Text
            variant="label"
            content={i18n.t('budgets.forms.new_budget.basic_information.title')}
          />
          <F0Text
            variant="body"
            color="secondary"
            content={i18n.t('budgets.forms.new_budget.basic_information.description')}
          />
        </F0Box>

        {legalEntities.length > 1 && (
          <Input
            disabled
            label={i18n.t('budgets.forms.new_budget.basic_information.legal_entity')}
            value={legalEntity?.legalName ?? ''}
            onChange={() => undefined}
            size="md"
          />
        )}

        <F0Form
          formRef={formRef}
          name="edit-expense-budget"
          schema={schema}
          defaultValues={defaultValues}
          submitConfig={{ type: 'default', hideSubmitButton: true }}
          onSubmit={handleSubmit}
        />

        {/* Archive Section */}
        <F0Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="between"
          padding="lg"
          borderRadius="md"
          border="default"
          borderColor="secondary"
        >
          <F0Box display="flex" flexDirection="column" gap="xs">
            <F0Text
              variant="label"
              content={
                budget.archived
                  ? i18n.t('budgets.forms.edit_budget.archive.unarchive')
                  : i18n.t('budgets.forms.edit_budget.archive.label')
              }
            />
            <F0Text
              variant="body"
              color="secondary"
              content={
                budget.archived
                  ? i18n.t('budgets.forms.edit_budget.archive.description_archived')
                  : i18n.t('budgets.forms.edit_budget.archive.description_unarchived')
              }
            />
          </F0Box>
          <Switch
            id="archive-budget-toggle"
            disabled={!canEdit}
            checked={archived}
            onCheckedChange={(checked) => setArchived(checked)}
          />
        </F0Box>
      </F0Box>
    </F0Dialog>
  )
}
